import { useEffect, useId, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { cn } from '@/utils/cn';
import { FieldLabel } from '@/components/ui/FieldLabel';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
}

export function Select({
  label,
  error,
  options,
  placeholder,
  value = '',
  onChange,
  disabled,
  className,
  id,
  name,
  required,
}: SelectProps) {
  const autoId = useId();
  const selectId = id ?? name ?? autoId;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label ?? placeholder ?? 'Select…';

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pick = (next: string) => {
    onChange?.(next);
    setOpen(false);
  };

  return (
    <div className={cn('relative w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={selectId} label={label} required={required} />
      <button
        type="button"
        id={selectId}
        name={name}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'input flex w-full items-center justify-between gap-2 text-left',
          !selected && 'text-slate-500 dark:text-slate-400',
          selected && 'text-slate-900 dark:text-white',
          error && 'border-red-500',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className="truncate">{displayLabel}</span>
        <KeyboardArrowDownIcon
          className={cn('shrink-0 text-slate-500 transition-transform dark:text-slate-400', open && 'rotate-180')}
          fontSize="small"
        />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 max-h-60 w-full min-w-[14rem] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-600 dark:bg-slate-800"
        >
          {placeholder && (
            <li role="option">
              <button
                type="button"
                className={cn(
                  'w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700',
                  value === '' && 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                )}
                onClick={() => pick('')}
              >
                {placeholder}
              </button>
            </li>
          )}
          {options.map((opt) => (
            <li key={opt.value} role="option" aria-selected={value === opt.value}>
              <button
                type="button"
                disabled={opt.disabled}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-700',
                  value === opt.value &&
                    'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                )}
                onClick={() => !opt.disabled && pick(opt.value)}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
